/**
 * The browser side of `/ai/prompts/*` (`bobcat/src/server-only/prompts/routes.ts`): the
 * Langfuse prompts a solution's AI nodes use.
 */
import { bobcatApiUrl } from '../../utils/bobcat-api';

export interface PromptView {
  name: string;
  version?: number;
  labels?: string[];
  prompt?: string;
  /** A template prompt every AI-Native solution reads; never edited here. */
  shared: boolean;
  /** This solution's own copy, which it can save new versions of. */
  editable: boolean;
  unavailable?: string;
}

export interface PromptScope {
  projectId: string;
  solutionKey: string;
}

const post = async <T>(path: string, body: object): Promise<T> => {
  const response = await fetch(bobcatApiUrl(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  const json = await response.json().catch(() => null);
  if (!response.ok) {
    const b = json as { detail?: string; error?: string } | null;
    throw new Error(b?.detail ?? b?.error ?? response.statusText);
  }
  return json as T;
};

export const readPrompts = (scope: PromptScope, names: string[]) =>
  post<{ prompts: PromptView[] }>('/ai/prompts/read', { ...scope, names }).then(
    (r) => r.prompts
  );

export const copyPrompt = (scope: PromptScope, name: string) =>
  post<{ prompt: PromptView }>('/ai/prompts/copy', { ...scope, name }).then(
    (r) => r.prompt
  );

export const savePromptText = (
  scope: PromptScope,
  { name, prompt }: { name: string; prompt: string }
) =>
  post<{ prompt: PromptView }>('/ai/prompts/save', {
    ...scope,
    name,
    prompt,
  }).then((r) => r.prompt);
